import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "../App";
import { useSubscription } from "../hooks/useSubscription";
import { Button } from "./ui/button";
import {
  Sparkles,
  BookOpen,
  History,
  Settings,
  Sun,
  Moon,
  ChevronDown,
  Star,
  Zap,
  GalleryHorizontal,
  Menu,
  Flame,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import { TOPIC_LABELS } from "../utils/constants";

/**
 * AppShell — shared header + left sidebar layout.
 *
 * Props:
 *   activeNav       "feed" | "shortlist" | "history" | "starred" | "carousels" | "settings"
 *   userTopics      string[]  topic ids shown in sidebar filter section (FeedPage only)
 *   selectedTopic   string | null  currently active topic filter
 *   onTopicFilter   (topic) => void  called when sidebar topic is clicked
 *   headerRight     ReactNode  extra content in the header right slot
 *   children        ReactNode  page content rendered in <main>
 */
const AppShell = ({
  children,
  activeNav = "feed",
  userTopics = [],
  selectedTopic = null,
  onTopicFilter = null,
  headerRight = null,
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isPro, isUltra, isTrialActive, trialDaysRemaining, postsRemainingToday } = useSubscription();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  /* ── Sidebar nav item ── */
  const NavItem = ({ label, icon, onClick, testId, isActive }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
        isActive
          ? "bg-primary/10 text-primary font-semibold"
          : "text-muted-foreground hover:bg-accent hover:text-foreground"
      }`}
    >
      <span className="w-4 h-4 shrink-0">{icon}</span>
      {label}
    </button>
  );

  /* ── Topic filter item ── */
  const TopicItem = ({ label, value, testId, onSelect }) => {
    const active = selectedTopic === value;
    return (
      <button
        onClick={() => {
          onTopicFilter?.(value);
          onSelect?.();
        }}
        data-testid={testId}
        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
          active
            ? "bg-primary/10 text-primary font-semibold"
            : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`}
      >
        {active && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
        {!active && <span className="w-1.5 h-1.5 shrink-0" />}
        {label}
      </button>
    );
  };

  /* ── Sidebar content — shared between desktop aside and mobile sheet ── */
  const SidebarContent = ({ onNavigate }) => {
    const go = (path) => {
      navigate(path);
      onNavigate?.();
    };
    return (
      <div className="p-3 flex flex-col gap-0.5 h-full">
        {/* ── Section: Library ── */}
        <p className="text-xs font-label font-semibold uppercase tracking-[0.15em] text-muted-foreground px-2.5 pt-3 pb-1.5">
          Library
        </p>

        <NavItem
          label="My Feed"
          icon={<Sparkles className="w-4 h-4" />}
          onClick={() => go("/feed")}
          testId="sidebar-feed"
          isActive={activeNav === "feed"}
        />
        <NavItem
          label="Saved Posts"
          icon={<Star className="w-4 h-4" />}
          onClick={() => go("/starred")}
          testId="sidebar-starred"
          isActive={activeNav === "starred"}
        />
        <NavItem
          label="Used Posts"
          icon={<History className="w-4 h-4" />}
          onClick={() => go("/history")}
          testId="sidebar-history"
          isActive={activeNav === "history"}
        />
        <NavItem
          label="Repurpose"
          icon={<BookOpen className="w-4 h-4" />}
          onClick={() => go("/shortlist")}
          testId="sidebar-shortlist"
          isActive={activeNav === "shortlist"}
        />
        <NavItem
          label="Carousels"
          icon={<GalleryHorizontal className="w-4 h-4" />}
          onClick={() => go("/carousels")}
          testId="sidebar-carousels"
          isActive={activeNav === "carousels"}
        />

        {/* ── Section: Filter by Topic — only on feed page ── */}
        {userTopics.length > 0 && onTopicFilter && (
          <>
            <p className="text-xs font-label font-semibold uppercase tracking-[0.15em] text-muted-foreground px-2.5 pt-4 pb-1.5">
              Filter by Topic
            </p>
            <TopicItem label="All Topics" value={null} testId="filter-all" onSelect={onNavigate} />
            {userTopics.map((topic) => (
              <TopicItem
                key={topic}
                label={TOPIC_LABELS[topic] || topic}
                value={topic}
                testId={`filter-${topic}`}
                onSelect={onNavigate}
              />
            ))}
            <TopicItem label="Others" value="__others" testId="filter-others" onSelect={onNavigate} />
            <button
              onClick={() => {
                onTopicFilter?.("__trending");
                onNavigate?.();
              }}
              data-testid="filter-trending"
              className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
                selectedTopic === "__trending"
                  ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 font-semibold"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              <Flame className="w-3.5 h-3.5 shrink-0" />
              Trending
            </button>
          </>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* ── Section: Account ── */}
        <div className="border-t border-border pt-3 mt-2 flex flex-col gap-0.5">
          <NavItem
            label="Settings"
            icon={<Settings className="w-4 h-4" />}
            onClick={() => go("/settings")}
            testId="sidebar-settings"
            isActive={activeNav === "settings"}
          />

          {/* Plan card — upgrade prompt */}
          {isPro ? (
            <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-primary/10 text-primary text-xs font-semibold mt-1">
              <Zap className="w-3.5 h-3.5 shrink-0" />
              {isUltra ? "Ultra Plan" : "Pro Plan"}
            </div>
          ) : isTrialActive ? (
            <div className="mt-1 px-2.5 py-2.5 rounded-lg bg-primary/5 border border-primary/15">
              <p className="text-xs font-semibold text-primary mb-0.5">
                Free Trial · <span className="font-mono tabular-nums">{trialDaysRemaining}d</span> left
              </p>
              {postsRemainingToday !== null && (
                <>
                  <div className="flex gap-0.5 my-1.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full ${
                          i < (5 - postsRemainingToday)
                            ? "bg-primary"
                            : "bg-border"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    <span className="font-mono tabular-nums">{postsRemainingToday} of 5</span> posts remaining today
                  </p>
                </>
              )}
              <button
                onClick={() => go("/pricing")}
                className="w-full py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-bold transition-opacity hover:opacity-90"
              >
                Upgrade to Pro →
              </button>
            </div>
          ) : (
            <div className="mt-1 px-2.5 py-2.5 rounded-lg bg-destructive/8 border border-destructive/20">
              <p className="text-xs font-semibold text-destructive mb-2">
                Trial ended
              </p>
              <button
                onClick={() => go("/pricing")}
                className="w-full py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-bold transition-opacity hover:opacity-90"
              >
                Subscribe to continue
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ── Header ── */}
      <header className="border-b border-border glass-panel bg-background/80 sticky top-0 z-50 shrink-0">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Brand + mobile menu */}
            <div className="flex items-center gap-1.5">
              {/* Mobile nav trigger */}
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden w-10 h-10 -ml-2"
                    aria-label="Open navigation menu"
                    data-testid="mobile-nav-trigger"
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0 flex flex-col overflow-y-auto">
                  <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                  <div className="flex items-center gap-2.5 px-5 pt-5 pb-1">
                    <img src="/logo.png" alt="" className="w-6 h-6" />
                    <span className="font-headline font-bold text-sm tracking-tight text-primary">
                      PostWriter
                    </span>
                  </div>
                  <div className="flex-1 flex flex-col min-h-0">
                    <SidebarContent onNavigate={() => setMobileNavOpen(false)} />
                  </div>
                </SheetContent>
              </Sheet>

              <button
                onClick={() => navigate("/feed")}
                className="flex items-center gap-2.5"
                aria-label="PostWriter — go to feed"
                data-testid="brand-home-link"
              >
                <img src="/logo.png" alt="PostWriter" className="w-7 h-7" />
                <span className="font-headline font-bold text-base tracking-tight text-primary">
                  PostWriter
                </span>
              </button>
            </div>

            {/* Right slot */}
            <div className="flex items-center gap-1.5">
              {headerRight}

              {/* Compact quota indicator — replaces intrusive banner */}
              {!isPro && isTrialActive && postsRemainingToday !== null && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-xs font-medium text-muted-foreground">
                  <span className="flex gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full ${
                          i < (5 - (postsRemainingToday ?? 0))
                            ? "bg-primary"
                            : "bg-border"
                        }`}
                      />
                    ))}
                  </span>
                  <span className="font-mono tabular-nums">{postsRemainingToday}/5 today</span>
                </div>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                className="w-10 h-10"
              >
                {theme === "dark" ? (
                  <Sun className="w-4 h-4" />
                ) : (
                  <Moon className="w-4 h-4" />
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-1.5 px-2 h-10"
                    data-testid="user-menu"
                    aria-label="Account menu"
                  >
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-7 h-7 rounded-full"
                      />
                    ) : (
                      <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold text-xs">
                        {user?.name?.charAt(0) || "U"}
                      </div>
                    )}
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="font-semibold text-sm">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  {/* Quota — visible here on mobile where the header pill is hidden */}
                  {!isPro && isTrialActive && postsRemainingToday !== null && (
                    <div className="sm:hidden px-2 pb-1.5">
                      <p className="text-xs text-muted-foreground">
                        <span className="font-mono tabular-nums">{postsRemainingToday}/5</span> posts left today
                      </p>
                    </div>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate("/history")}
                    data-testid="history-link"
                  >
                    <History className="w-4 h-4 mr-2" />
                    Used Posts
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/settings")}
                    data-testid="settings-link"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    className="text-destructive"
                  >
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0">
        {/* Left Sidebar — desktop only */}
        <aside className="hidden lg:flex flex-col w-52 xl:w-56 bg-background border-r border-border shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto">
          <SidebarContent />
        </aside>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
